import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import {
  HomeIcon,
  MegaphoneIcon,
  DocumentTextIcon,
  WalletIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Campaigns', href: '/campaigns', icon: MegaphoneIcon },
  { name: 'Templates', href: '/templates', icon: DocumentTextIcon },
  { name: 'Wallets', href: '/wallets', icon: WalletIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Mobile sidebar */}
      <div className="lg:hidden">
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <div
          className={`fixed inset-y-0 left-0 z-50 w-72 bg-dark-800 transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-dark-700">
            <Link to="/dashboard" className="flex items-center gap-2">
              <span className="text-2xl font-display text-primary-400">FlashDoom</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 text-gray-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="mt-6 px-3">
            <ul className="space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname.startsWith(item.href);
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'bg-primary-400/10 text-primary-400 shadow-[inset_2px_0_0_0_#8cbe06]'
                          : 'text-gray-400 hover:text-white hover:bg-dark-700'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      {item.name}
                      {isActive && (
                        <span className="ml-auto w-2 h-2 bg-primary-400 rounded-full" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User section */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-dark-700">
            <div className="flex items-center gap-3 p-3 bg-dark-900 rounded-lg">
              <div className="w-10 h-10 bg-primary-400/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-400 font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.name || user?.email?.split('@')[0]}
                </p>
                <p className="text-xs text-green-400">Free Plan</p>
              </div>
            </div>
            <button
              onClick={() => logout()}
              className="flex items-center gap-2 w-full mt-3 px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:fixed lg:inset-y-0 lg:flex-col lg:w-72">
        <div className="flex grow flex-col bg-dark-800 border-r border-dark-700">
          {/* Logo */}
          <div className="flex items-center h-20 px-6 border-b border-dark-700">
            <Link to="/dashboard" className="flex items-center gap-2">
              <span className="text-2xl font-display text-primary-400">FlashDoom</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 overflow-y-auto">
            <ul className="space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname.startsWith(item.href);
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'bg-primary-400/10 text-primary-400 shadow-[inset_3px_0_0_0_#8cbe06]'
                          : 'text-gray-400 hover:text-white hover:bg-dark-700'
                      }`}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      {item.name}
                      {isActive && (
                        <span className="ml-auto w-2 h-2 bg-primary-400 rounded-full" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-dark-700">
            <div className="p-4 bg-dark-900 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-400/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-400 font-semibold">
                    {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {user?.name || user?.email?.split('@')[0]}
                  </p>
                  <p className="text-xs text-green-400">Free Plan - Unlimited</p>
                </div>
              </div>
              <button
                onClick={() => logout()}
                className="flex items-center justify-center gap-2 w-full mt-3 px-3 py-2 text-sm text-gray-400 hover:text-white bg-dark-800 hover:bg-dark-700 rounded-lg transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Mobile header */}
        <header className="sticky top-0 z-40 flex h-16 items-center gap-x-4 border-b border-dark-700 bg-dark-900/95 backdrop-blur-lg px-4 lg:hidden">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-400 hover:text-white"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <span className="text-xl font-display text-primary-400">FlashDoom</span>
          </div>
        </header>

        <main className="py-6 px-4 sm:px-6 lg:px-8">
          {children}
        </main>
      </div>
    </div>
  );
}
